<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { autoExtractAndWarp } from '../scanner';
import { startLiveScanner, warpByCorners, type Corner, type LiveScannerHandle } from '../live-scanner';
import LiveOverlay from './LiveOverlay.vue';

const emit = defineEmits<{
  /** Углы найдены — отдаём готовый выпрямленный JPEG */
  captured: [jpeg: Blob];
  /** Углы НЕ найдены — отдаём сырой canvas, App.vue покажет ManualCornerEditor */
  manualNeeded: [sourceCanvas: HTMLCanvasElement];
  cancelled: [];
}>();

const videoEl = ref<HTMLVideoElement | null>(null);
const fileInputEl = ref<HTMLInputElement | null>(null);
const cameraError = ref<string | null>(null);
const isProcessing = ref(false);
let stream: MediaStream | null = null;

// Live-mode state (TASK Adobe-Scan-like, 2026-05-12)
const liveCorners = ref<Corner[] | null>(null);
const videoW = ref<number>(0);
const videoH = ref<number>(0);
const autoCaptureEnabled = ref<boolean>(true);
const capturing = ref<boolean>(false);  // визуальный флаг для зелёной рамки
let liveHandle: LiveScannerHandle | null = null;

const canUseGetUserMedia = ref<boolean>(
  typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
);

onMounted(async () => {
  if (!canUseGetUserMedia.value) {
    cameraError.value = 'getUserMedia недоступен в этом WebView';
    return;
  }
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
      audio: false,
    });
    if (videoEl.value) {
      videoEl.value.srcObject = stream;
      await videoEl.value.play();
      videoW.value = videoEl.value.videoWidth || 1280;
      videoH.value = videoEl.value.videoHeight || 720;

      // Запускаем live-детект (Adobe-Scan-like)
      try {
        liveHandle = await startLiveScanner({
          video: videoEl.value,
          onQuad: (corners) => {
            liveCorners.value = corners;
          },
          onAutoCapture: async (sourceCanvas, corners) => {
            await onLiveCapture(sourceCanvas, corners);
          },
          autoCaptureEnabled: () => autoCaptureEnabled.value && !isProcessing.value,
        });
      } catch (err) {
        // jscanify не загрузился / другая ошибка — оставляем старый manual-flow
        console.warn('live-scanner failed, fallback manual', err);
      }
    }
  } catch (err) {
    cameraError.value = err instanceof Error ? err.message : String(err);
    console.error('camera.failed', err);
  }
});

onBeforeUnmount(() => {
  if (liveHandle) {
    liveHandle.stop();
    liveHandle = null;
  }
  if (stream) {
    stream.getTracks().forEach((t) => t.stop());
    stream = null;
  }
});

async function onLiveCapture(sourceCanvas: HTMLCanvasElement, corners: Corner[]) {
  if (isProcessing.value) return;
  isProcessing.value = true;
  capturing.value = true;
  // Haptic + flash
  try {
    navigator.vibrate?.(40);
  } catch {
    /* ignore */
  }
  try {
    const jpeg = await warpByCorners(sourceCanvas, corners);
    emit('captured', jpeg);
  } catch (err) {
    console.error('live-capture warp failed', err);
    cameraError.value = 'Не удалось обработать фото';
  } finally {
    isProcessing.value = false;
    setTimeout(() => (capturing.value = false), 250);  // короткая «зелёная вспышка»
  }
}

async function snap() {
  // Ручной shutter — берёт сглаженные углы из live-режима если есть;
  // иначе откатываемся на старый autoExtractAndWarp.
  if (!videoEl.value || !stream || isProcessing.value) return;
  isProcessing.value = true;
  capturing.value = true;
  try {
    navigator.vibrate?.(30);
  } catch {
    /* ignore */
  }
  try {
    let sourceCanvas: HTMLCanvasElement | null = null;
    if (liveHandle) {
      sourceCanvas = liveHandle.getCurrentFrameCanvas();
    } else {
      // Fallback: создаём канвас на лету
      const c = document.createElement('canvas');
      c.width = videoEl.value.videoWidth;
      c.height = videoEl.value.videoHeight;
      const ctx = c.getContext('2d');
      if (!ctx) {
        cameraError.value = 'Canvas недоступен';
        return;
      }
      ctx.drawImage(videoEl.value, 0, 0);
      sourceCanvas = c;
    }
    if (!sourceCanvas) return;

    // Если рамка визуально показана — варпим по ней (быстро и точно)
    if (liveCorners.value && liveCorners.value.length === 4) {
      const jpeg = await warpByCorners(sourceCanvas, liveCorners.value);
      emit('captured', jpeg);
      return;
    }

    // Иначе — старый pipeline: jscanify findContour + auto-warp / manual fallback
    const result = await autoExtractAndWarp(sourceCanvas);
    if (result.autoDetected) {
      emit('captured', result.jpeg);
    } else {
      emit('manualNeeded', sourceCanvas);
    }
  } finally {
    isProcessing.value = false;
    setTimeout(() => (capturing.value = false), 250);
  }
}

async function onFilePicked(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file) return;
  isProcessing.value = true;
  try {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Не удалось прочитать фото'));
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      cameraError.value = 'Canvas недоступен';
      return;
    }
    ctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(img.src);

    const result = await autoExtractAndWarp(canvas);
    if (result.autoDetected) {
      emit('captured', result.jpeg);
    } else {
      emit('manualNeeded', canvas);
    }
  } catch (err) {
    cameraError.value = err instanceof Error ? err.message : String(err);
  } finally {
    isProcessing.value = false;
    if (fileInputEl.value) fileInputEl.value.value = '';
  }
}
</script>

<template>
  <div class="fixed inset-0 z-50 bg-black flex flex-col">
    <!-- Камера + live overlay -->
    <div class="flex-1 relative flex items-center justify-center bg-black overflow-hidden">
      <video
        v-if="!cameraError"
        ref="videoEl"
        playsinline
        autoplay
        muted
        class="w-full h-full object-contain"
      />
      <!-- Adobe-Scan-like SVG поверх видео -->
      <LiveOverlay
        v-if="!cameraError && videoW > 0"
        :corners="liveCorners"
        :video-width="videoW"
        :video-height="videoH"
        :capturing="capturing"
      />
      <!-- Подсказка пользователю -->
      <div
        v-if="!cameraError && !liveCorners && !capturing"
        class="absolute top-4 left-4 right-4 text-center text-white/85 text-sm pointer-events-none"
      >
        Наведите камеру на документ
      </div>
      <div
        v-if="!cameraError && liveCorners && !capturing && autoCaptureEnabled"
        class="absolute top-4 left-4 right-4 text-center text-yellow-300 text-sm pointer-events-none"
      >
        Держите ровно — снимок через мгновение…
      </div>

      <!-- Flash при capture -->
      <div
        v-if="capturing"
        class="absolute inset-0 bg-white opacity-50 pointer-events-none"
        style="animation: flash 250ms ease-out;"
      ></div>

      <!-- Fallback: камера не работает -->
      <div
        v-if="cameraError"
        class="absolute inset-0 flex flex-col items-center justify-center text-white p-6 text-center gap-4"
      >
        <div class="text-5xl">📷</div>
        <p class="text-base">
          Камера недоступна в этом приложении.<br />
          Сделайте фото или выберите из галереи:
        </p>
        <input
          ref="fileInputEl"
          type="file"
          accept="image/*"
          capture="environment"
          class="hidden"
          @change="onFilePicked"
        />
        <button class="btn min-w-[180px]" @click="fileInputEl?.click()">
          📸 Открыть камеру / галерею
        </button>
        <p class="text-xs text-white/60 mt-2">{{ cameraError }}</p>
      </div>
    </div>

    <!-- Контролы -->
    <div v-if="!cameraError" class="bg-black/80 p-4 flex gap-2 items-center justify-between safe-bottom">
      <button class="btn btn-secondary text-white border-white" @click="emit('cancelled')">
        Отмена
      </button>
      <button
        class="auto-toggle"
        :class="{ active: autoCaptureEnabled }"
        @click="autoCaptureEnabled = !autoCaptureEnabled"
        :aria-label="autoCaptureEnabled ? 'Авто-снимок включён' : 'Авто-снимок выключен'"
      >
        ⚡ Авто
      </button>
      <button
        class="btn min-w-[120px]"
        :disabled="isProcessing"
        @click="snap"
      >
        {{ isProcessing ? '...' : '📸 Снять' }}
      </button>
    </div>
    <div v-else class="bg-black/80 p-4 flex justify-center safe-bottom">
      <button class="btn btn-secondary text-white border-white" @click="emit('cancelled')">
        Отмена
      </button>
    </div>
  </div>
</template>

<style scoped>
.safe-bottom {
  padding-bottom: max(env(safe-area-inset-bottom), 1rem);
}
.auto-toggle {
  padding: 0.5rem 0.9rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: white;
  font-size: 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 150ms ease;
}
.auto-toggle.active {
  background: rgba(255, 204, 0, 0.85);
  color: #1a1a1a;
  border-color: #ffcc00;
  box-shadow: 0 0 12px rgba(255, 204, 0, 0.4);
}
@keyframes flash {
  0% { opacity: 0.6; }
  100% { opacity: 0; }
}
</style>
